import { useCallback, useEffect, useState } from "react";
import { Link2, Search, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { subscribeDeckMessages } from "@/lib/deckChannel";
import { countDecksUsingNugget, listNuggets } from "@/lib/idb";
import { nuggetDisplayName } from "@/lib/nugget";
import { deleteNugget } from "@/lib/nuggetActions";
import { cn } from "@/lib/utils";
import type { NuggetIndexEntry } from "@/types/nugget";

// §17.4.5 The local nugget shelf, as a right-panel mode beside library / style / transition /
// logo / diagram / gantt.
//
// A thumbnail grid and a title search, and the same insert semantics the Gridd Library has
// (§8.4.2): **a pick appends a NEW slide, it never overwrites the selected one.** That rule is
// the one thing about this panel worth being dogmatic about — the selected slide is wherever
// the user's scroll position happened to land, and overwriting it is a destructive action
// triggered by a browse.
//
// The tiles render `nuggetIndex.thumbDataUrl`, captured off the deck's own slide thumbnails
// rather than by a second capturer (see `DeckEditor`'s nugget-thumb effect). A nugget saved
// from a slide whose thumbnail had not been taken yet therefore shows a placeholder until the
// next time some deck renders it — which is the honest state, and cheaper than standing up an
// offscreen capturer for the shelf.
//
// Phase 1 is the LOCAL shelf. §17.9's Local / Organization split, and everything behind it, is
// phase 2 and is deliberately not stubbed here: an empty "Organization" tab is a promise, and
// §17.9.3 is explicit that signed-out-or-no-server must not read as an error state.
export function NuggetPanel({
  onInsert,
  readOnly,
}: {
  // Called with the nugget to append. The panel does not know how to add a slide — that is
  // the editor's job, and it is the same handler shape the Library pick uses.
  onInsert: (nuggetId: string) => void;
  readOnly: boolean;
}) {
  const [rows, setRows] = useState<NuggetIndexEntry[] | null>(null);
  const [query, setQuery] = useState("");

  const refresh = useCallback(() => {
    void listNuggets().then(setRows);
  }, []);

  useEffect(refresh, [refresh]);

  // §17.2.4 The shelf is shared state: another window may rename a nugget (by retitling the
  // slide in a different deck) or delete one while this panel is open. Both ride the channel
  // the rest of the app already uses, so the list stays honest without polling.
  useEffect(() => {
    return subscribeDeckMessages((msg) => {
      if (msg.type === "nugget-updated" || msg.type === "nugget-deleted") {
        refresh();
      }
    });
  }, [refresh]);

  const needle = query.trim().toLowerCase();
  const visible = (rows ?? []).filter((row) =>
    needle ? nuggetDisplayName(row.name).toLowerCase().includes(needle) : true,
  );

  // §17.6.1 Deleting a nugget from the shelf NEVER touches a deck: every deck keeps its slide,
  // exactly as it is, and drops the dead link the next time it opens (§17.2.5). The count is
  // in the dialog because "this is used in 3 decks" is the one fact that changes the answer,
  // and it is scanned at dialog time rather than maintained — a reference count that drifts is
  // worse than no count at all.
  const remove = async (row: NuggetIndexEntry) => {
    const used = await countDecksUsingNugget(row.nuggetId);
    const name = nuggetDisplayName(row.name);
    const usage =
      used === 0
        ? "No deck uses it."
        : used === 1
          ? "1 deck uses it; that deck keeps its own copy of the slide and stops sharing."
          : `${used} decks use it; each keeps its own copy of the slide and stops sharing.`;
    if (
      !window.confirm(
        `Delete “${name}” from the nugget shelf? ${usage} This cannot be undone.`,
      )
    ) {
      return;
    }
    await deleteNugget(row.nuggetId);
    refresh();
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-slate-200 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search nuggets"
            className="pl-8"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {rows === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          // The empty state says how one gets here, because nothing in this panel can create
          // a nugget — the action lives on the slide, which is the only place that has a
          // slide to save.
          <div className="rounded-md border border-dashed border-slate-200 p-4 text-sm text-slate-500">
            <p className="font-medium text-slate-700">No nuggets yet</p>
            <p className="mt-1">
              Save a slide to the shelf from its toolbar, then add it to any
              deck. Editing it in one deck updates it in all of them.
            </p>
          </div>
        ) : visible.length === 0 ? (
          <p className="text-sm text-slate-500">
            No nugget matches “{query.trim()}”.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3">
            {visible.map((row) => (
              <li
                key={row.nuggetId}
                className="group overflow-hidden rounded-md border border-slate-200"
              >
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => onInsert(row.nuggetId)}
                  className={cn(
                    "block w-full text-left",
                    readOnly
                      ? "cursor-not-allowed opacity-60"
                      : "hover:bg-slate-50",
                  )}
                  title={
                    readOnly
                      ? "This deck is open in another window"
                      : "Add as a new slide"
                  }
                >
                  {row.thumbDataUrl ? (
                    <img
                      src={row.thumbDataUrl}
                      alt=""
                      className="aspect-video w-full border-b border-slate-200 object-cover"
                    />
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center border-b border-slate-200 bg-slate-50 text-xs text-slate-400">
                      No preview yet
                    </div>
                  )}
                  <div className="flex items-start gap-2 px-3 py-2">
                    <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">
                        {nuggetDisplayName(row.name)}
                      </p>
                      <p className="text-xs text-slate-500">
                        Updated {new Date(row.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </button>
                <div className="flex justify-end border-t border-slate-100 px-2 py-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => void remove(row)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete from shelf
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
